import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClipboardList, Loader2, Mail, MessageSquare } from "lucide-react";

// Gedeeld verzendlog + voortgangsbalk: elk bulk-venster (event/klanten/webinar) gebruikt deze componenten.

export const DELIVERY_CHIP = {
  opened: { cls: "bg-sky-100 text-sky-700 border-sky-200", label: "Geopend" },
  clicked: { cls: "bg-indigo-100 text-indigo-700 border-indigo-200", label: "Geklikt" },
  delivered: { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Afgeleverd" },
  bounced: { cls: "bg-rose-100 text-rose-700 border-rose-200", label: "Gebounced" },
  complained: { cls: "bg-rose-100 text-rose-700 border-rose-200", label: "Spam-klacht" },
  failed: { cls: "bg-rose-100 text-rose-700 border-rose-200", label: "Mislukt" },
  delayed: { cls: "bg-amber-100 text-amber-700 border-amber-200", label: "Vertraagd" },
  no_email: { cls: "bg-amber-100 text-amber-700 border-amber-200", label: "Geen e-mail" },
  no_phone: { cls: "bg-amber-100 text-amber-700 border-amber-200", label: "Geen nummer" },
  onbekend: { cls: "bg-slate-100 text-slate-500 border-slate-200", label: "Onbekend" },
  sent: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Verstuurd" },
};

export function DeliveryChip({ r }) {
  const chip = DELIVERY_CHIP[r.status] || DELIVERY_CHIP.sent;
  const extra = r.status === "opened" && r.open_count > 1 ? ` ×${r.open_count}`
    : r.tracked === false && r.status === "sent" ? "" : "";
  return (
    <span title={r.bounce_reason || r.error || undefined}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${chip.cls}`}>
      {chip.label}{extra}
    </span>
  );
}

// Universele voortgangsbalk voor alle bulk-verzendingen (e-mail + WhatsApp).
export function BulkProgress({ job, channel = "whatsapp", testId = "bulk-progress" }) {
  if (!job) return null;
  const skipped = (job.no_phone || 0) + (job.no_email || 0) + (job.skipped || 0);
  const processed = (job.sent || 0) + (job.failed || 0) + skipped;
  const pct = job.total ? Math.min(100, Math.round((processed / job.total) * 100)) : 100;
  const busy = job.status === "running";
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${channel === "whatsapp" ? "bg-emerald-500" : "bg-blue-600"} transition-all`}
          style={{ width: `${pct}%` }} />
      </div>
      <p className="text-sm text-slate-600" data-testid={`${testId}-text`}>
        {job.status === "quiet" ? "Stille uren (23:00–06:00) — gaat om 06:00 automatisch verder. " : busy ? "Bezig… " : "✅ Klaar: "}
        <b className={channel === "whatsapp" ? "text-emerald-700" : "text-blue-700"}>{job.sent || 0} verstuurd</b>
        {(job.failed || 0) > 0 && <span className="text-red-600 font-medium"> · {job.failed} mislukt</span>}
        {skipped > 0 && <span className="text-amber-600"> · {skipped} zonder {channel === "whatsapp" ? "nummer" : "e-mailadres"}</span>}
        <span className="text-slate-400"> · {processed}/{job.total || 0}</span>
      </p>
    </div>
  );
}

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

// Universeel verzendlog-venster: tabblad E-mail | WhatsApp, per verzending de status per persoon.
// Ververst elke 3 sec zolang de geselecteerde verzending loopt (daarna niet meer).
export default function SendLogDialog({ scope, eventId, open, onClose, initialChannel = "email", initialJobId = null }) {
  const [channel, setChannel] = useState(initialChannel);
  const [jobs, setJobs] = useState(null);
  const [jobId, setJobId] = useState(initialJobId);
  const [detail, setDetail] = useState(null);
  const pollRef = useRef(null);

  const loadJobs = useCallback((ch, keepJobId) => {
    setJobs(null);
    api.get("/comm/send-log/jobs", { params: { scope, channel: ch, event_id: eventId || undefined } })
      .then(({ data }) => {
        const list = data.jobs || [];
        setJobs(list);
        setJobId(keepJobId && list.some((j) => j.id === keepJobId) ? keepJobId : (list[0]?.id || null));
      })
      .catch(() => setJobs([]));
  }, [scope, eventId]);

  useEffect(() => {
    if (!open) { clearTimeout(pollRef.current); return; }
    setChannel(initialChannel);
    setDetail(null);
    loadJobs(initialChannel, initialJobId);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDetail = useCallback((id) => {
    api.get(`/comm/send-log/jobs/${id}`)
      .then(({ data }) => {
        setDetail(data);
        clearTimeout(pollRef.current);
        if (data.job?.status === "running" || data.job?.status === "quiet") {
          pollRef.current = setTimeout(() => loadDetail(id), 3000);
        }
      })
      .catch(() => toast.error("Verzendlog laden mislukt"));
  }, []);

  useEffect(() => {
    clearTimeout(pollRef.current);
    setDetail(null);
    if (open && jobId) loadDetail(jobId);
    return () => clearTimeout(pollRef.current);
  }, [jobId, open, loadDetail]);

  const pickChannel = (ch) => { setChannel(ch); setDetail(null); setJobId(null); loadJobs(ch); };
  const live = detail && (detail.job.status === "running" || detail.job.status === "quiet");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="send-log-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5 text-blue-700" /> Verzendlog</DialogTitle>
          <DialogDescription>
            Per verzending zie je wie is bereikt: WhatsApp (verstuurd/mislukt/geen nummer) en e-mail met live Resend-tracking.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-1.5" data-testid="send-log-tabs">
          {[["email", "E-mail", Mail], ["whatsapp", "WhatsApp", MessageSquare]].map(([key, label, Icon]) => (
            <button key={key} type="button" onClick={() => pickChannel(key)} data-testid={`send-log-tab-${key}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${channel === key
                ? key === "whatsapp" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-sky-500 bg-sky-50 text-sky-700"
                : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>
        {jobs === null ? (
          <div className="py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : jobs.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500" data-testid="send-log-empty">
            Nog geen {channel === "whatsapp" ? "WhatsApp-berichten" : "e-mails"} verstuurd.
          </p>
        ) : (
          <div className="space-y-3">
            <Select value={jobId || ""} onValueChange={setJobId}>
              <SelectTrigger data-testid="send-log-job-select"><SelectValue placeholder="Kies een verzending…" /></SelectTrigger>
              <SelectContent>
                {jobs.map((j) => (
                  <SelectItem key={j.id} value={j.id}>
                    {fmtWhen(j.created_at)} · {j.label || (channel === "whatsapp" ? "WhatsApp" : "E-mail")} · {j.total} ontv.{j.status === "running" ? " · bezig…" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!detail ? (
              <div className="py-6 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
            ) : (
              <>
                {live && (
                  <p className="text-xs text-emerald-600 flex items-center gap-1" data-testid="send-log-live">
                    <Loader2 className="h-3 w-3 animate-spin" /> Live — ververst elke 3 sec zolang de verzending loopt
                  </p>
                )}
                <BulkProgress job={detail.job} channel={channel} testId="send-log-progress" />
                {channel === "email" && (
                  <div className="flex flex-wrap gap-2 text-xs" data-testid="send-log-counts">
                    <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-emerald-700">{detail.counts.delivered} afgeleverd</span>
                    <span className="rounded-full bg-sky-100 px-2.5 py-1 text-sky-700">{detail.counts.opened} geopend</span>
                    {detail.counts.problems > 0 && (
                      <span className="rounded-full bg-rose-100 px-2.5 py-1 text-rose-700">{detail.counts.problems} probleem{detail.counts.problems === 1 ? "" : "en"}</span>
                    )}
                  </div>
                )}
                {detail.job.simulated && (
                  <p className="text-xs text-amber-600">Simulatie: echte verzendingen staan uit in deze omgeving.</p>
                )}
                {detail.rows.length === 0 ? (
                  <p className="text-xs text-amber-600" data-testid="send-log-no-rows">
                    Deze verzending is van vóór de log-update — alleen de totalen zijn bekend.
                  </p>
                ) : (
                  <div className="max-h-96 overflow-y-auto rounded-lg border border-slate-100">
                    <table className="w-full text-sm" data-testid="send-log-table">
                      <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold">Naam</th>
                          <th className="px-3 py-2 text-left font-semibold">{channel === "whatsapp" ? "Nummer" : "E-mail"}</th>
                          <th className="px-3 py-2 text-left font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.rows.map((r, i) => (
                          <tr key={i} className="border-t border-slate-100" data-testid={`send-log-row-${i}`}>
                            <td className="px-3 py-1.5 text-slate-800">{r.name || "—"}</td>
                            <td className="px-3 py-1.5 text-slate-500">
                              {r.contact || "—"}
                              {r.extra_to?.length > 0 && (
                                <span className="mt-0.5 block text-[11px] text-violet-600" data-testid={`send-log-partner-${i}`}>
                                  💑 partner meegemaild: {r.extra_to.join(", ")}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-1.5"><DeliveryChip r={r} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
